import { productStories, type ProductStory } from "./product-stories";
// Editorial capability descriptions. Evidence and commercial boundaries are
// recorded in docs/marketing-evidence.md; published offers determine availability.
export type ProductEditorial = {
  fit: string;
  scenarioTitle: string;
  scenarios: [string, string, string][];
  checklist: string[];
  faq: [string, string][];
  diagram: { nodes: [string, string][]; note: string };
};
export type MarketingProduct = {
  story: ProductStory;
  editorial: ProductEditorial;
  slug: string;
  name: string;
  category: string;
  short: string;
  title: string;
  description: string;
  useCase: string;
  docs: string;
  features: [string, string][];
  steps: [string, string][];
  boundary: string;
  related: string[];
};
export const products: MarketingProduct[] = [
  {
    slug: "virtual-machines",
    story: productStories["virtual-machines"],
    editorial: {
      fit: "Choose a VM when the application needs system packages, a particular Linux setup or direct control of a guest operating system.",
      scenarioTitle: "Room for the stack you need.",
      scenarios: [
        [
          "A custom application stack",
          "Install your web server, application runtime and supporting packages in one guest environment. Keep setup instructions alongside your project.",
          "Runtime versions, public ports and an update routine.",
        ],
        [
          "A database you administer",
          "Give database software its own VM and restrict application connections to the services that need access.",
          "Working set, storage capacity and a tested independent backup.",
        ],
        [
          "A separate test environment",
          "Prepare a guest for a release candidate or a dependency upgrade before applying the same changes to production.",
          "Representative test data, configuration differences and resource lifetime.",
        ],
      ],
      checklist: [
        "Select an image compatible with your application and initialization script.",
        "Keep the SSH key available and confirm an alternative console access path.",
        "Test application recovery and maintain backups outside the guest.",
      ],
      faq: [
        [
          "How does a VM differ from a container?",
          "A VM gives you a guest operating system to administer. A container starts with a packaged application image. Choose the VM when the operating system and system packages are part of what you need to control.",
        ],
        [
          "Can I run PostgreSQL or another database?",
          "You can install compatible database software in your guest. Your team handles its configuration, updates, access, backups and recovery. This is a self-managed application deployment.",
        ],
        [
          "When should I use a snapshot?",
          "Where supported, an on-demand snapshot can provide a restore point before a change. Plan for application consistency and retain an independent backup; restoring a snapshot can replace newer data.",
        ],
      ],
      diagram: {
        nodes: [
          [
            "Image & access",
            "Select a supported Linux image, SSH key and initial configuration.",
          ],
          [
            "Your virtual machine",
            "Run the guest OS, system packages and application on QEMU / KVM.",
          ],
          [
            "Application endpoint",
            "Connect through the configured network and verify a real request.",
          ],
        ],
        note: "Guest software, application configuration and data remain under your administration.",
      },
    },
    name: "Virtual machines",
    category: "Compute",
    short: "Your operating system. Your environment.",
    title: "An environment you can make your own.",
    description:
      "Run applications on QEMU/KVM virtual machines with control over the operating system, configuration and software you install.",
    useCase:
      "For applications that need their own Linux environment, system packages or a database you administer yourself.",
    docs: "/docs/vps",
    features: [
      [
        "Prepare the guest with cloud-init",
        "Start from an available Linux image and supply an SSH key and supported initialization input. Keep repeatable setup instructions with your application so new environments begin with the configuration you intend.",
      ],
      [
        "Control the operating system",
        "Install packages, configure system services and choose the application runtime inside your guest. A VM is useful when the operating system itself is part of the deployment you need to manage.",
      ],
      [
        "Operate through the console",
        "Inspect resource state, manage power and use the browser console for guest access. Follow the result of each action before investigating the application running inside the machine.",
      ],
      [
        "Create an on-demand restore point",
        "Use snapshot and restore operations where the service supports them. Review application consistency and the data a restore replaces; keep independent backups for recovery beyond the guest.",
      ],
    ],
    steps: [
      [
        "Choose an offer",
        "Review the image, resource allocation, region and terms in the published offer.",
      ],
      [
        "Prepare access",
        "Select an SSH key and provide the initial configuration supported by the deployment form.",
      ],
      [
        "Operate the guest",
        "Connect, install your application and maintain its operating system and data.",
      ],
    ],
    boundary:
      "You manage guest updates, application security and backups. An on-demand snapshot is not an independent backup policy. Hardware specifications and available actions depend on the selected offer.",
    related: ["networking", "cloud-shell"],
  },
  {
    slug: "containers",
    story: productStories["containers"],
    editorial: {
      fit: "Choose a container when your software is already packaged as an OCI image and its configuration can be supplied at deployment.",
      scenarioTitle: "One image. A clearly defined service.",
      scenarios: [
        [
          "An application API",
          "Package the API and its dependencies in an image. Configure the listening port and the connections to its backing services.",
          "Image architecture, environment variables and endpoint access.",
        ],
        [
          "A background worker",
          "Run a compatible worker image that connects to the queue or application service you operate.",
          "Outbound access, retry behavior and orderly shutdown.",
        ],
        [
          "A release evaluation",
          "Run a versioned image with separate test configuration to review an application change.",
          "Image version, test data and persistent storage behavior.",
        ],
      ],
      checklist: [
        "Confirm that the runtime can access a compatible image.",
        "Document required variables, listening ports and external dependencies.",
        "Check how application data survives restarts and image changes.",
      ],
      faq: [
        [
          "Can I use an existing application image?",
          "Start with an OCI image compatible with the selected runtime and its architecture. Check registry access and supported configuration fields before ordering.",
        ],
        [
          "Where should my application keep data?",
          "Review the persistence options of the chosen offer. Treat files inside a container as application state that needs an explicit storage and recovery plan.",
        ],
        [
          "Who updates the application dependencies?",
          "Your team maintains and rebuilds the image, selects the version to deploy and checks the result. Keep a known working version and a rollback procedure.",
        ],
      ],
      diagram: {
        nodes: [
          ["OCI image", "Package the application and its dependencies."],
          [
            "Runtime configuration",
            "Supply supported environment, ports and storage options.",
          ],
          [
            "Podman workload",
            "Inspect service state, application logs and connectivity.",
          ],
        ],
        note: "Persistence, image access and restart behavior depend on your selected configuration.",
      },
    },
    name: "Containers",
    category: "Runtime",
    short: "Bring an OCI image. Configure your service.",
    title: "Bring your application in a container.",
    description:
      "Use Podman-backed OCI workloads to package services with their dependencies and manage their configuration from the console.",
    useCase:
      "For APIs, web services and workers already packaged as container images.",
    docs: "/docs/games",
    features: [
      [
        "Deploy a packaged application",
        "Use an OCI image that contains the application and its dependencies. An explicit image version gives your team a recognizable release artifact to deploy and investigate.",
      ],
      [
        "Configure the service boundary",
        "Supply required environment variables and exposed ports through the available configuration fields. Define how the process reaches databases, queues and other services you operate.",
      ],
      [
        "Follow startup and lifecycle",
        "Inspect the deployed container state and use available lifecycle actions from the console. Application startup, network reachability and persistent data each need their own check.",
      ],
      [
        "Read application logs",
        "Use the available log view to investigate startup errors and request handling. Maintain the image, dependencies and log content as part of your application release process.",
      ],
    ],
    steps: [
      [
        "Select a workload",
        "Choose a published container offer and a compatible image.",
      ],
      [
        "Configure the runtime",
        "Set the supported environment and network options for your application.",
      ],
      [
        "Inspect the service",
        "Follow logs, check connectivity and adjust your application configuration.",
      ],
    ],
    boundary:
      "You maintain your images, dependencies and application data. Rootless operation, persistence and scaling characteristics must be verified for the chosen runtime; they are not universal promises.",
    related: ["functions", "networking"],
  },
  {
    slug: "functions",
    story: productStories["functions"],
    editorial: {
      fit: "Choose a function for a focused HTTP task that fits the selected execution limits and can be tested independently of the rest of your application.",
      scenarioTitle: "Small tasks with a clear input and result.",
      scenarios: [
        [
          "A webhook handler",
          "Receive an application event, validate the request and return a deliberate response to the sender.",
          "Request authentication, duplicate delivery and error responses.",
        ],
        [
          "A lightweight HTTP endpoint",
          "Expose a small transformation or validation step as a separate request-driven workload.",
          "Input size, execution timeout and access to dependencies.",
        ],
        [
          "An integration step",
          "Connect two application services with focused logic and explicit failure handling.",
          "Upstream timeouts, credentials and safe retries.",
        ],
      ],
      checklist: [
        "Confirm the supported runtime, resource limits and execution timeout.",
        "Define how callers authenticate and what constitutes a valid request.",
        "Test success, invalid input, duplicate requests and unavailable dependencies.",
      ],
      faq: [
        [
          "Is a function suitable for a long-running worker?",
          "Compare the workload with the execution model and timeout of the offer. A container or VM can be a better fit when a service needs to keep running between requests.",
        ],
        [
          "How should I handle webhook retries?",
          "Design the handler to recognize duplicate events and avoid repeating side effects. The caller may retry when a response is delayed or a connection fails.",
        ],
        [
          "How are functions billed?",
          "Review the actual billing unit and included quantities in the selected offer. Invocation-based pricing, scale-to-zero and automatic scaling should only be assumed when specified there.",
        ],
      ],
      diagram: {
        nodes: [
          ["HTTP request", "A caller invokes the configured endpoint."],
          [
            "Function handler",
            "Validate input and run your application logic.",
          ],
          [
            "Response & inspection",
            "Return a result and inspect available execution logs.",
          ],
        ],
        note: "This illustrates an application flow, not a promise of a particular scaling or billing model.",
      },
    },
    name: "Functions",
    category: "Runtime",
    short: "A focused runtime for request-driven code.",
    title: "Give a single task its own runtime.",
    description:
      "Deploy request-driven functions through the platform and inspect their execution using the available runtime tools.",
    useCase:
      "For webhooks and small HTTP tasks that fit the execution model of a published function offer.",
    docs: "/docs/functions",
    features: [
      [
        "Deploy a focused handler",
        "Give a small HTTP task its own source and configuration. Keep integration logic separate when it has a different purpose or change cycle from the rest of the application.",
      ],
      [
        "Expose an invocation endpoint",
        "Use the configured endpoint to call the handler from your application or an external system. Your handler owns input validation, request authentication and the response it returns.",
      ],
      [
        "Investigate execution behavior",
        "Inspect function state and available logs to understand successful and failed calls. Exercise malformed input and unavailable dependencies as well as the expected request.",
      ],
      [
        "Choose around execution limits",
        "Review the selected configuration for resources, timeout behavior and billing units. Keep durable data in an appropriate external service and design effects to handle caller retries.",
      ],
    ],
    steps: [
      [
        "Check the runtime",
        "Review supported configuration and execution constraints in the selected offer.",
      ],
      [
        "Deploy the function",
        "Provide the required function configuration through the Resource Composer.",
      ],
      [
        "Test the invocation",
        "Call the configured endpoint and verify both successful and failed requests.",
      ],
    ],
    boundary:
      "Check timeouts, resource limits, invocation access and billing units before choosing this runtime. Do not assume scale-to-zero, automatic scaling or millisecond billing unless the offer specifies it.",
    related: ["containers", "dns"],
  },
  {
    slug: "networking",
    story: productStories["networking"],
    editorial: {
      fit: "Plan networking around the connections your application actually needs: its public entry point, private dependencies and administrative access.",
      scenarioTitle: "Give every connection a purpose.",
      scenarios: [
        [
          "A public web service",
          "Allow traffic to the application entry point and keep administrative access limited to the intended sources.",
          "Listening ports, service interfaces and allowed sources.",
        ],
        [
          "Private application dependencies",
          "Connect application services through supported networks and subnets. Keep a database reachable only where it is needed.",
          "Address ranges, routes and both allowed and denied traffic.",
        ],
        [
          "A controlled configuration change",
          "Record the current route and firewall settings before changing how a workload can be reached.",
          "An access path, verification steps and a way to restore the previous configuration.",
        ],
      ],
      checklist: [
        "Map inbound, internal and outbound traffic before creating rules.",
        "Check address space for overlaps with connected environments.",
        "Verify connectivity after the operation applies, including connections that should fail.",
      ],
      faq: [
        [
          "Do I need a public address for every service?",
          "Design around the entry points your application needs. Where the selected environment supports private connections, dependencies can use those paths while the public service handles incoming requests.",
        ],
        [
          "Does saving a rule mean traffic has changed?",
          "Check the applied operation state and then test the connection. A saved control-plane configuration and the resulting network behavior are separate observations.",
        ],
        [
          "Which network options are available?",
          "Available subnets, routes, NAT and address pools depend on the environment and service configuration. Review the offered options before relying on a particular traffic path.",
        ],
      ],
      diagram: {
        nodes: [
          [
            "Client connection",
            "An intended source reaches the application entry point.",
          ],
          [
            "Routes & firewall",
            "Configured traffic paths and access rules determine reachability.",
          ],
          [
            "Application subnet",
            "Supported interfaces connect the services that need to communicate.",
          ],
        ],
        note: "An example traffic path. Firewall rules do not establish upstream DDoS mitigation.",
      },
    },
    name: "Networking",
    category: "Connect",
    short: "Define how your workloads connect.",
    title: "Make the connections explicit.",
    description:
      "Organize networks and subnets, inspect interfaces, and manage routing and firewall configuration from one place.",
    useCase:
      "For applications whose public entry points and private service connections need to be configured deliberately.",
    docs: "/docs/networking",
    features: [
      [
        "Organize address space",
        "Create supported networks and subnets, then connect the relevant service interfaces. Use an address plan that leaves each application dependency and administrative path understandable.",
      ],
      [
        "Define routes and NAT",
        "Manage available route tables and NAT gateways to describe where traffic goes. The chosen network mode and address pools determine which paths the environment can provide.",
      ],
      [
        "Express access with firewall rules",
        "Describe allowed traffic by the controls available in your environment. Review application listeners and guest firewalls alongside platform rules when checking access.",
      ],
      [
        "Follow applied configuration",
        "Inspect the operation behind a network change and verify the connections it affects. A saved request, an applied rule and an application response describe different stages of the same change.",
      ],
    ],
    steps: [
      [
        "Map traffic",
        "Identify public entry points, internal connections and required outbound access.",
      ],
      [
        "Configure the network",
        "Create the supported network resources, routes and firewall rules.",
      ],
      [
        "Verify reachability",
        "Check service connectivity and observe whether configuration changes have applied.",
      ],
    ],
    boundary:
      "Network features depend on node configuration and available address pools. Firewall controls are not a promise of upstream DDoS protection, private interconnect or global Anycast routing.",
    related: ["virtual-machines", "dns"],
  },
  {
    slug: "dns",
    story: productStories["dns"],
    editorial: {
      fit: "Use DNS to give services a stable name. Follow each record change through to resolution, then check the certificate presented by the endpoint.",
      scenarioTitle: "From a domain name to a working endpoint.",
      scenarios: [
        [
          "Connect an application domain",
          "Create the supported record that points the application name to its configured endpoint.",
          "Record type, destination, TTL and authoritative delegation.",
        ],
        [
          "Move an endpoint",
          "Prepare a record change as part of a release or migration and verify resolution from outside the application network.",
          "Cached responses, application readiness and a rollback value.",
        ],
        [
          "Review service identity",
          "Follow supported certificate operations and confirm the intended certificate at the endpoint.",
          "Domain validation, service binding and renewal configuration.",
        ],
      ],
      checklist: [
        "Confirm domain ownership and the authoritative nameservers in use.",
        "Review record names, values and TTL before submitting changes.",
        "Verify external DNS resolution and the certificate served by the application.",
      ],
      faq: [
        [
          "Do I need to transfer my domain registration?",
          "Domain registration remains with your registrar. Follow the supplied verification and delegation instructions to configure the DNS workflow for your environment.",
        ],
        [
          "Why do clients still see the previous DNS value?",
          "Resolvers can retain cached responses according to their TTL. Check the authoritative result and allow for caches when planning a cutover.",
        ],
        [
          "Is certificate issuance the last step?",
          "Verify the certificate binding and what the live endpoint presents. Issuance alone does not confirm installation. Automated processing also requires the corresponding configured services.",
        ],
      ],
      diagram: {
        nodes: [
          [
            "Domain & record",
            "Define the name, record type, destination and TTL.",
          ],
          [
            "Applied DNS change",
            "Follow the operation and check authoritative resolution.",
          ],
          [
            "Named service",
            "Verify reachability and the certificate served by the endpoint.",
          ],
        ],
        note: "DNS resolution and certificate installation are separate checks in a service rollout.",
      },
    },
    name: "DNS & certificates",
    category: "Connect",
    short: "Connect names, records and service identity.",
    title: "Bring your services into reach.",
    description:
      "Manage DNS zones and records, then follow certificate operations for supported service bindings in the console.",
    useCase:
      "For teams connecting application endpoints to domain names and managing their certificate lifecycle.",
    docs: "/docs/domains",
    features: [
      [
        "Manage zones and records",
        "Keep the records that point clients to your services in one console. Review the name, type, destination and TTL together before publishing a change.",
      ],
      [
        "Track record changes",
        "Follow a DNS change through its operation state, then check authoritative resolution. Existing resolver caches can continue serving the previous value until its TTL expires.",
      ],
      [
        "Coordinate certificate operations",
        "Use supported issuance, renewal and service-binding workflows to manage endpoint identity. Automated processing depends on the configured certificate services and CA profile.",
      ],
      [
        "Prepare an endpoint cutover",
        "Plan DNS and HTTPS together when moving an application. Make the new destination ready, account for cached records and confirm the certificate served at the endpoint.",
      ],
    ],
    steps: [
      [
        "Prepare the domain",
        "Confirm domain ownership and the nameservers or records required for your configuration.",
      ],
      [
        "Publish DNS changes",
        "Create records and check their applied state and external resolution.",
      ],
      [
        "Review certificates",
        "Configure supported certificate operations and confirm installation at the endpoint.",
      ],
    ],
    boundary:
      "Domain registration remains with your registrar. DNSSEC and certificate automation require the corresponding configured services. Issuance support does not guarantee that a certificate controller is enabled in every environment.",
    related: ["networking", "virtual-machines"],
  },
  {
    slug: "cloud-shell",
    story: productStories["cloud-shell"],
    editorial: {
      fit: "Use Cloud Shell for a bounded operational task when you want the platform CLI available from the browser alongside the console.",
      scenarioTitle: "A workspace for the task at hand.",
      scenarios: [
        [
          "Inspect account resources",
          "Use supported CLI reads to find the services associated with your account and review their resource context.",
          "The correct account and the command output you need.",
        ],
        [
          "Follow a documented workflow",
          "Keep a guide and command help alongside the shell while working through an available platform operation.",
          "Command support, permissions and any required confirmation.",
        ],
        [
          "Work from another workstation",
          "Open an authenticated browser workspace without installing the platform CLI on that local computer.",
          "A trusted browser, session deadline and saving your work.",
        ],
      ],
      checklist: [
        "Check workspace availability and the displayed session deadline.",
        "Use CLI help to confirm the command and its options.",
        "Save needed work and close the session when the task is complete.",
      ],
      faq: [
        [
          "Does Cloud Shell give me root access to a VM?",
          "Cloud Shell is a separate authenticated workspace. Guest administration requires the appropriate service credentials and a configured connection path.",
        ],
        [
          "Can I leave an application running in the shell?",
          "Use a VM, container or function for application hosting. Cloud Shell is an interactive workspace with an explicit lifecycle and session limits.",
        ],
        [
          "Can I run every platform operation from the CLI?",
          "Use command help to discover supported operations. Access follows account policy and the relevant write confirmation requirements. A readable resource does not imply permission to modify it.",
        ],
      ],
      diagram: {
        nodes: [
          [
            "Authenticated console",
            "Check workspace availability for your account.",
          ],
          [
            "Browser workspace",
            "Open the terminal and discover supported CLI commands.",
          ],
          [
            "Account resources",
            "Inspect the resources and operations your session can access.",
          ],
        ],
        note: "The browser workspace is separate from guest operating systems and their credentials.",
      },
    },
    name: "Cloud Shell",
    category: "Operate",
    short: "A terminal workspace inside your browser.",
    title: "Keep a terminal close to your infrastructure.",
    description:
      "Open an authenticated browser workspace to use the platform CLI and inspect the resources available to your account.",
    useCase:
      "For checking platform resources and running supported command-line workflows without setting up another local workstation.",
    docs: "/docs/cloud-shell",
    features: [
      [
        "Open a browser workspace",
        "Start from the authenticated console when Cloud Shell is available to your account. The workspace brings a terminal alongside your platform resources without a local CLI installation.",
      ],
      [
        "Discover supported commands",
        "Use Ecocloud CLI help to inspect commands and options. Find the operation that fits the task before running it, with access governed by your account policy.",
      ],
      [
        "Inspect account resources",
        "List services and use supported reads to understand the environment you are working with. Keep the command output alongside the relevant setup or troubleshooting guide.",
      ],
      [
        "Work within a visible lifecycle",
        "Review workspace state and the session deadline. Save needed output and close the session after the task; use a workload runtime for applications that must keep running.",
      ],
    ],
    steps: [
      [
        "Open the console",
        "Sign in and check Cloud Shell availability for your account.",
      ],
      [
        "Start a workspace",
        "Read the session limits and open the authenticated terminal.",
      ],
      [
        "Run a supported task",
        "Use CLI help to find commands and save work before the workspace deadline.",
      ],
    ],
    boundary:
      "Cloud Shell is a separate workspace, not automatic root access to every VM. Access and write operations follow account policy. Treat it as a bounded interactive session, not a permanent application runtime.",
    related: ["virtual-machines", "containers"],
  },
];
export function getProduct(slug: string) {
  return products.find((p) => p.slug === slug);
}
